/* eslint-disable no-unused-vars */
import _ceil from 'lodash/ceil';
import _find from 'lodash/find';
import _floor from 'lodash/floor';
import _filter from 'lodash/filter';
import _flatMap from 'lodash/flatMap';
import moment from 'moment';
import L from 'leaflet';
import d3Legacy from 'd3-legacy';
import { getCurrentD3Locale } from 'js/helpers/dateTimeAndNumberFormatting';
import { appendEastResizeIcon } from 'js/components/legacy/TimeGraph';
import { graphColors as GRAPH_COLORS } from 'json/shared_styles.json';
var GENERATOR_MAP_INFO_MAP = {
  bunker_fuel: {
    color: GRAPH_COLORS[0],
    title: 'Bunker Fuel'
  },
  wind: {
    color: GRAPH_COLORS[1],
    title: 'Wind'
  },
  hydro: {
    color: GRAPH_COLORS[2],
    title: 'Hydro'
  },
  geothermal: {
    color: GRAPH_COLORS[3],
    title: 'Geothermal'
  },
  biomass: {
    color: GRAPH_COLORS[4],
    title: 'Biomass'
  },
  demand: {
    color: GRAPH_COLORS[5],
    title: 'Demand'
  }
};
var GEO_JSON_MARKER_OPTIONS_DEFAULT = {
  weight: 1,
  opacity: 1,
  fillOpacity: 0.6
};
var GENERATOR_MAP_UNIT = 'MWh';
var SLIDER_HEIGHT = 17;
var SLIDER_TICK_MARGIN = 10;
var SLIDER_HANDLE_WIDTH = 19;
var SLIDER_MARGIN = {
  left: SLIDER_HANDLE_WIDTH / 2,
  right: SLIDER_HANDLE_WIDTH / 2
};
var bisectDate = d3Legacy.bisector(function (d) {
  return d.date;
}).left;

var getSliderWidth = function getSliderWidth(svg) {
  return svg.node().getBoundingClientRect().width - SLIDER_MARGIN.left - SLIDER_MARGIN.right;
};

export var initializePlot = function initializePlot(ctx) {
  var mapa = L.map(ctx.props.plotID);
  if (!mapa) return null;
  var map = mapa.setView([12.136389, -86.251389], 7);
  L.tileLayer('http://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}.png', {
    attribution: '©OpenStreetMap, ©CartoDB'
  }).addTo(map);
  L.tileLayer('http://{s}.basemaps.cartocdn.com/rastertiles/voyager_labels_under/{z}/{x}/{y}.png', {
    attribution: '©OpenStreetMap, ©CartoDB'
  }).addTo(map);
  map.scrollWheelZoom.disable();
  var props = ctx.props;
  var fullDateExtent = d3Legacy.extent(props.data.map(function (a) {
    return a.date;
  })); // save redux state

  var svg = d3Legacy.select(ctx.refs.sliderSvg); // set up SVG

  var sliderWidth = getSliderWidth(svg);
  var xScale = d3Legacy.scale.linear().domain([new Date(fullDateExtent[0]), new Date(fullDateExtent[1])]).range([0, sliderWidth]);
  var slider = svg // set up SLIDER
  .append('g').attr('class', 'cosmos-brush cosmos-brush--x').attr('transform', "translate(".concat(SLIDER_MARGIN.left, ",").concat(SLIDER_HEIGHT / 2 + 1, ")"));
  slider.append('line').attr('class', 'background--line').style('stroke-width', SLIDER_HEIGHT).attr('x1', xScale.range()[0] - SLIDER_HANDLE_WIDTH / 2).attr('x2', xScale.range()[1] + SLIDER_HANDLE_WIDTH / 2);
  var sliderSelection = slider.append('line').attr('class', 'extent--line').style('stroke-width', SLIDER_HEIGHT).attr('x1', xScale.range()[0] - SLIDER_HANDLE_WIDTH / 2).attr('x2', xScale.range()[0] - SLIDER_HANDLE_WIDTH / 2);
  var tickContainer = slider.insert('g', '.track-overlay').attr('class', 'ticks cosmos-axis cosmos-axis--x').attr('transform', "translate(0,".concat(SLIDER_HEIGHT + SLIDER_TICK_MARGIN, ")"));
  var sliderHandle = slider.insert('g', '.track-overlay').attr('transform', "translate(0,".concat(-2.5, ")")).attr('class', 'cosmos-slider-handle');
  appendEastResizeIcon(sliderHandle);
  var drag = d3Legacy.behavior.drag().origin(function () {
    return {
      x: 0,
      y: 0
    };
  }).on('dragstart', onDragGenerator).on('drag', onDragGenerator);
  var sliderOverlay = slider.append('g').append('line').attr('class', 'track-overlay').style('stroke-width', SLIDER_HEIGHT).attr('x1', xScale.range()[0] - SLIDER_HANDLE_WIDTH / 2).attr('x2', xScale.range()[1] + SLIDER_HANDLE_WIDTH / 2).call(drag); // set up SLIDER ends

  var ticks = tickContainer.selectAll('text').data(getTickDates(props, xScale, svg)); // set up TICK

  ticks.enter().append('text').attr('class', 'tick-text');
  ticks.exit().remove();

  var formatX = function formatX(x) {
    return xScale(new Date(x));
  };

  ticks.attr('x', formatX).attr('text-anchor', 'middle').text(formatSliderDate); // set up TICK ends

  var currentData = Array.isArray(props.currentState.data) ? props.currentState.data[0] : props.currentState.data.values; // set up currentGeneratorData

  var loadMarkers = [];
  var keys = Object.keys(currentData.values);
  keys.map(function (k) {
    var genMarker = L.geoJson(currentData.values[k], {
      pointToLayer: pointToLayer
    });
    loadMarkers.push(genMarker);
    genMarker.addTo(map);
  });
  props.actions.addEntry(props.countryID, props.plotID, 'markers', loadMarkers);
  props.actions.addEntry(props.countryID, props.plotID, 'map', map);
  props.actions.setTotalDateRange(props.countryID, props.plotID, [moment(fullDateExtent[0]), moment(fullDateExtent[1])]); // set up FullDateExtent

  ctx.setState({
    loaded: true
  });

  function onDragGenerator() {
    d3Legacy.event.sourceEvent.stopPropagation();
    var xPosition = xScale.invert(d3Legacy.mouse(this)[0]);
    var neww = new Date(xPosition).toISOString().slice(0, 10); // Todo

    var pixelPosition = formatX(neww);
    sliderHandle.attr('transform', "translate(".concat(pixelPosition, ",").concat(-2.5, ")"));
    sliderSelection.attr('x2', pixelPosition - SLIDER_HANDLE_WIDTH / 2);
    var currentIndex = bisectDate(props.data, "".concat(neww, " 00:00:00")); // Todo

    props.actions.setCurrentData(props.countryID, props.plotID, currentIndex); // Fix

    updatePlot(ctx);
  }

  return {
    map: map,
    fullDateExtent: fullDateExtent,
    loadMarkers: loadMarkers
  };
};
export var updatePlot = function updatePlot(ctx) {
  var _ctx$props$currentSta = ctx.props.currentState,
      map = _ctx$props$currentSta.map,
      markers = _ctx$props$currentSta.markers,
      data = _ctx$props$currentSta.data;
  var loadMarkers = [];
  markers.map(function (a) {
    return map.removeLayer(a);
  });
  var keys = Object.keys(data.values.values);
  keys.map(function (k) {
    var genMarker = L.geoJson(data.values.values[k], {
      pointToLayer: pointToLayer
    });
    loadMarkers.push(genMarker);
    genMarker.addTo(map);
  });
  ctx.props.actions.addEntry(ctx.props.countryID, ctx.props.plotID, 'markers', loadMarkers);
};

var pointToLayer = function pointToLayer(feature, latlng, props) {
  var geojsonMarkerOptions = Object.assign({}, GEO_JSON_MARKER_OPTIONS_DEFAULT, {
    radius: feature.properties.marker_size,
    color: GENERATOR_MAP_INFO_MAP[feature.properties.type].color,
    fillColor: GENERATOR_MAP_INFO_MAP[feature.properties.type].color,
    title: GENERATOR_MAP_INFO_MAP[feature.properties.type].title
  });
  return bindMarkerHoverToPopupOpenClose(L.circleMarker(latlng, geojsonMarkerOptions).bindPopup("<div class='data-page__section__graph--map__popup-content' style=\"border-color: ".concat(geojsonMarkerOptions.color, "\">\n        <div>").concat(geojsonMarkerOptions.title, "</div>\n        <div>").concat(feature.properties.name, ": ").concat(feature.properties.energia, " ").concat(GENERATOR_MAP_UNIT, "\n        </div>\n        </div>")));
};

var bindMarkerHoverToPopupOpenClose = function bindMarkerHoverToPopupOpenClose(marker) {
  marker.on('mouseover', function () {
    this.openPopup();
  });
  marker.on('mouseout', function () {
    this.closePopup();
  });
  return marker;
};

var getTickDates = function getTickDates(props, xScale, svg) {
  var ticks = [];

  var maxNumberOfXTicks = _floor(getSliderWidth(svg) / 80);

  var incrementBy = _ceil(props.data.length / maxNumberOfXTicks);

  var finalDate = mapPlotDataPointToDate(props.data[props.data.length - 1]);
  var currentIndex = 0;
  var currentDataPoint = props.data[currentIndex];

  while (currentIndex < props.data.length - 1) {
    var current = mapPlotDataPointToDate(currentDataPoint);
    ticks.push(current);
    currentIndex += incrementBy;
    currentDataPoint = props.data[currentIndex];
  }

  var conf = xScale(ticks[ticks.length - 1]) + 35 >= xScale(finalDate);

  if (conf) {
    // 35 is approximate width of a tick currently
    ticks.pop();
  }

  ticks.push(finalDate);
  return ticks;
}; //--------------------------------------------------


function mapPlotDataPointToDate(plotDataPoint) {
  return plotDataPoint.date;
}

function formatSliderDate(sliderDate) {
  return getCurrentD3Locale().timeFormat('%m/%y')(new Date(sliderDate));
}